import { loadConfig } from './config';

/**
 * Sign-in against the Cognito hosted UI using authorization code + PKCE.
 *
 * PKCE rather than the implicit flow because the app is a public client with no secret: the
 * code verifier proves the token request came from the same browser that started the
 * sign-in. Tokens are kept in sessionStorage, which is scoped to the tab and cleared when it
 * closes; the refresh token is the only long-lived credential and it never leaves this origin.
 */

const STORAGE = {
  verifier: 'unschool.pkce_verifier',
  state: 'unschool.oauth_state',
  tokens: 'unschool.tokens',
} as const;

interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

function readTokens(): TokenSet | undefined {
  const raw = sessionStorage.getItem(STORAGE.tokens);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    // A corrupt entry is not recoverable and silently ignoring it would loop the user
    // through sign-in forever with no explanation.
    console.warn('Stored session was unreadable and has been cleared.');
    sessionStorage.removeItem(STORAGE.tokens);
    return undefined;
  }
}

function writeTokens(tokens: TokenSet): void {
  sessionStorage.setItem(STORAGE.tokens, JSON.stringify(tokens));
}

export function signOutLocal(): void {
  sessionStorage.removeItem(STORAGE.tokens);
}

/**
 * Sign out for real: drop this tab's tokens, then end the Cognito session too.
 *
 * Clearing sessionStorage alone is not signing out. The hosted UI keeps its own session
 * cookie, so the next "Sign in with Google" completes silently against the same identity and
 * lands the person exactly where they started. That is merely confusing for a member, but for
 * someone who signed in with an uninvited address it is a trap: the only screen they can
 * reach tells them to sign out and try another account, and nothing they do changes who they
 * are signed in as.
 *
 * This does not, and should not, sign them out of Google itself -- that is the browser's
 * session for every Google property, not ours to end. It does not need to: ending the
 * Cognito session is enough for Google to show its account chooser on the next sign-in,
 * verified against the live site with two accounts signed in. A browser with only one
 * Google account will be sent straight back through as that account, which is why the gate
 * says to add another at Google rather than promising a chooser.
 *
 * Asking Google for the chooser explicitly is not available to us: Cognito does not forward
 * `prompt` to the identity provider. Confirmed by sending prompt=select_account to
 * /oauth2/authorize and reading the redirect, which carries only access_type, client_id,
 * redirect_uri, response_type, scope and state.
 */
export async function signOutFederated(): Promise<void> {
  sessionStorage.removeItem(STORAGE.tokens);
  sessionStorage.removeItem(STORAGE.verifier);
  sessionStorage.removeItem(STORAGE.state);

  const config = await loadConfig();
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
}

export function isSignedIn(): boolean {
  return readTokens() !== undefined;
}

/** Begin sign-in by redirecting to the hosted UI. */
export async function signIn(): Promise<void> {
  const config = await loadConfig();

  const verifier = randomString(64);
  const state = randomString(24);
  sessionStorage.setItem(STORAGE.verifier, verifier);
  sessionStorage.setItem(STORAGE.state, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: await sha256Base64Url(verifier),
    code_challenge_method: 'S256',
    // Straight to Google: there is no password to type, so the chooser is friction.
    identity_provider: 'Google',
  });

  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

/** Complete sign-in on the callback route. Returns where the app should navigate next. */
export async function completeSignIn(): Promise<string> {
  const config = await loadConfig();
  const url = new URL(window.location.href);

  const error = url.searchParams.get('error');
  if (error) {
    throw new Error(
      `Sign-in failed: ${url.searchParams.get('error_description') ?? error}`,
    );
  }

  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const expectedState = sessionStorage.getItem(STORAGE.state);
  const verifier = sessionStorage.getItem(STORAGE.verifier);

  if (!code) throw new Error('Sign-in callback did not include an authorization code');
  if (!state || state !== expectedState) {
    // A mismatched state is the signature of a CSRF attempt, not a glitch to retry through.
    throw new Error('Sign-in state did not match. Start again from the sign-in page.');
  }
  if (!verifier) throw new Error('Sign-in verifier was lost. Start again from the sign-in page.');

  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.userPoolClientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed (${response.status}): ${await response.text()}`);
  }

  const body = (await response.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  writeTokens({
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + body.expires_in * 1000,
  });

  sessionStorage.removeItem(STORAGE.verifier);
  sessionStorage.removeItem(STORAGE.state);

  // Nothing to carry through: an invited address is joined by the server during sign-in.
  return '/';
}

/**
 * A valid id token, refreshing a little before expiry. The API Gateway authorizer validates
 * this token, so an expired one is a 401 rather than a silent failure.
 */
export async function idToken(): Promise<string> {
  const tokens = readTokens();
  if (!tokens) throw new Error('Not signed in');

  if (Date.now() < tokens.expiresAt - 60_000) return tokens.idToken;

  if (!tokens.refreshToken) {
    signOutLocal();
    throw new Error('Your session expired. Sign in again.');
  }

  const config = await loadConfig();
  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.userPoolClientId,
      refresh_token: tokens.refreshToken,
    }),
  });

  if (!response.ok) {
    signOutLocal();
    throw new Error('Your session expired. Sign in again.');
  }

  const body = (await response.json()) as {
    id_token: string;
    access_token: string;
    expires_in: number;
  };

  const refreshed: TokenSet = {
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + body.expires_in * 1000,
  };
  writeTokens(refreshed);
  return refreshed.idToken;
}

function randomString(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64UrlEncode(buffer);
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
